// src/pages/Signup.js — 회원가입 2단계: ① 계정 → ② 신체 정보·영양 목표(건너뛰기 가능)
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { checkUserid, userSignup } from '../api/dietApi';
import { useToast } from '../components/ui/Toast';
import { ACT_LV, rateToGrams, recommendKcal } from './goal';
import { fmt } from '../components/ui/Nutrients';
import './Auth.css';

// 서버(TbUsersDto) 검증 규칙과 같게
const RULES = {
  userId: [/^[a-zA-Z0-9]{5,20}$/, '아이디는 5~20자의 영문 또는 숫자만 쓸 수 있습니다.'],
  passWd: [/^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*\W)(?=\S+$).{8,40}$/, '비밀번호는 8~40자로 영문·숫자·특수문자를 모두 넣어 주세요.'],
  userName: [/^[가-힣]{1,20}$/, '이름은 한글 20자 이내로 입력해 주세요.'],
  phoneNo: [/^[0-9]{11}$/, '휴대폰 번호는 숫자 11자리입니다.'],
  email: [/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}$/, '이메일 형식이 올바르지 않습니다.'],
};

const EMPTY = {
  userId: '', passWd: '', confirmPassWd: '', userName: '', gender: '', birthDate: '', phoneNo: '', email: '',
  height: '', weight: '', actLv: '', wantKcal: '',
};

export default function Signup() {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [idState, setIdState] = useState('');      // '', 'checking', 'ok', 'taken'
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: k === 'phoneNo' ? v.replace(/[^0-9]/g, '') : v }));
    if (k === 'userId') setIdState('');            // 아이디를 바꾸면 중복확인 다시
  };
  const blur = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const errorOf = (k) => {
    const v = form[k];
    if (k === 'confirmPassWd') return v && v !== form.passWd ? '비밀번호가 일치하지 않습니다.' : '';
    if (k === 'gender' || k === 'birthDate') return v ? '' : '필수 항목입니다.';
    const r = RULES[k];
    return r && !r[0].test(v) ? r[1] : '';
  };
  const show = (k) => (touched[k] ? errorOf(k) : '');
  const step1Keys = ['userId', 'passWd', 'confirmPassWd', 'userName', 'gender', 'birthDate', 'phoneNo', 'email'];

  const checkId = async () => {
    blur('userId');
    if (errorOf('userId')) return;
    setIdState('checking');
    try {
      const res = await checkUserid(form.userId);
      setIdState(res.status === 200 ? 'ok' : 'taken');
    } catch {
      setIdState('');
      toast('중복확인 중 오류가 발생했습니다.', { type: 'error' });
    }
  };

  const next = (e) => {
    e.preventDefault();
    setTouched(Object.fromEntries(step1Keys.map((k) => [k, true])));
    if (step1Keys.some((k) => errorOf(k)) || !form.confirmPassWd) return;
    if (idState !== 'ok') { toast('아이디 중복확인을 해주세요.', { type: 'error' }); return; }
    setStep(2);
  };

  const suggested = recommendKcal(form);

  const submit = async (withBody) => {
    setLoading(true); setServerErrors([]);
    try {
      const { confirmPassWd, ...rest } = form;
      const payload = { ...rest };
      ['height', 'weight', 'actLv', 'wantKcal'].forEach((k) => { payload[k] = withBody && payload[k] !== '' ? Number(payload[k]) : undefined; });
      // 칼로리를 정했으면 기본 비율(균형 50·25·25)도 같이 넣어 바로 추천을 받을 수 있게 한다
      if (payload.wantKcal) Object.assign(payload, { carboRate: 50, protRate: 25, fatRate: 25 });
      await userSignup(payload);
      toast('회원가입을 완료했습니다.', { type: 'success' });
      navigate('/login', { replace: true, state: { signedUp: true, userId: form.userId } });
    } catch (err) {
      const data = err?.response?.data;
      if (Array.isArray(data) && data.length) { setServerErrors(data.map((x) => x.defaultMessage)); setStep(1); } else {
        toast(data?.message || (typeof data === 'string' ? data : '') || '회원가입에 실패했습니다.', { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const field = (k, label, props = {}, extra = null) => (
    <label className="auth-field">
      <span>{label}</span>
      <div className="auth-input-row">
        <input className={`input${show(k) ? ' invalid' : ''}`} value={form[k]} onChange={(e) => set(k, e.target.value)} onBlur={() => blur(k)} {...props} />
        {extra}
      </div>
      {show(k) && <em className="auth-field-error">{show(k)}</em>}
    </label>
  );

  return (
    <div className="auth">
      <div className="auth-card wide">
        <div className="auth-steps">
          <span className={step === 1 ? 'on' : 'done'}>1 계정</span>
          <span className={step === 2 ? 'on' : ''}>2 신체 정보</span>
        </div>

        {step === 1 ? (
          <form onSubmit={next}>
            <h2>회원가입</h2>
            {serverErrors.length > 0 && <ul className="auth-error">{serverErrors.map((m) => <li key={m}>{m}</li>)}</ul>}
            {field('userId', '아이디', { autoComplete: 'username', autoFocus: true }, (
              <button className="btn" type="button" onClick={checkId} disabled={idState === 'checking'}>
                {idState === 'checking' ? '확인 중…' : '중복확인'}
              </button>
            ))}
            {idState === 'ok' && <p className="auth-ok">사용할 수 있는 아이디입니다.</p>}
            {idState === 'taken' && <p className="auth-field-error">이미 사용 중인 아이디입니다.</p>}
            {field('passWd', '비밀번호', { type: 'password', autoComplete: 'new-password' })}
            {field('confirmPassWd', '비밀번호 확인', { type: 'password', autoComplete: 'new-password' })}
            {field('userName', '이름', { placeholder: '한글' })}
            <div className="auth-field">
              <span>성별</span>
              <div className="auth-seg">
                {[['M', '남성'], ['F', '여성']].map(([v, l]) => (
                  <button key={v} type="button" className={form.gender === v ? 'on' : ''} onClick={() => { set('gender', v); blur('gender'); }}>{l}</button>
                ))}
              </div>
              {show('gender') && <em className="auth-field-error">{show('gender')}</em>}
            </div>
            {field('birthDate', '생년월일', { type: 'date' })}
            {field('phoneNo', '휴대폰 번호', { inputMode: 'numeric', placeholder: '01012345678', maxLength: 11 })}
            {field('email', '이메일', { type: 'email', autoComplete: 'email' })}
            <button className="btn primary auth-submit" type="submit">다음</button>
            <p className="auth-alt">이미 계정이 있으신가요? <Link to="/login">로그인</Link></p>
          </form>
        ) : (
          <div>
            <h2>신체 정보 <small>선택</small></h2>
            <p className="auth-note">입력하면 권장 칼로리를 계산해 바로 식단 추천을 받을 수 있어요. 나중에 마이페이지에서 바꿀 수 있습니다.</p>
            <div className="auth-two">
              {field('height', '키 (cm)', { type: 'number', min: 0 })}
              {field('weight', '몸무게 (kg)', { type: 'number', min: 0 })}
            </div>
            <label className="auth-field">
              <span>활동량</span>
              <select className="select" value={form.actLv} onChange={(e) => set('actLv', e.target.value)}>
                <option value="">선택</option>
                {ACT_LV.map((a) => <option key={a.v} value={a.v}>{a.l} ({a.d})</option>)}
              </select>
            </label>
            <label className="auth-field">
              <span>하루 희망 칼로리</span>
              <div className="auth-input-row">
                <input className="input" type="number" min="0" value={form.wantKcal} onChange={(e) => set('wantKcal', e.target.value)} placeholder="비워 두면 나중에" />
                {suggested && <button className="btn" type="button" onClick={() => set('wantKcal', String(suggested))}>권장 {fmt(suggested)} kcal</button>}
              </div>
              {form.wantKcal && (() => {
                const g = rateToGrams(form.wantKcal, 50, 25, 25);
                return <em className="auth-hint">균형 비율(탄 50 · 단 25 · 지 25%)로 시작 → 탄수 {fmt(g.carbo)}g · 단백 {fmt(g.protein)}g · 지방 {fmt(g.fat)}g</em>;
              })()}
            </label>
            <div className="auth-step2-actions">
              <button className="btn ghost" type="button" onClick={() => setStep(1)}>이전</button>
              <button className="btn" type="button" disabled={loading} onClick={() => submit(false)}>건너뛰고 가입</button>
              <button className="btn primary" type="button" disabled={loading} onClick={() => submit(true)}>{loading ? '가입 중…' : '가입하기'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
